"use client";

import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {addContact, listContacts, contactCopy} from "@/lib/user/contacts";

export type GoogleMapsContact = {value: string; source: string; source_url?: string; type?: string; confidence?: number};
export type GoogleMapsSocial = {type: string; url: string};

export type GoogleMapsPlace = {
  id: string;
  name: string;
  category?: string;
  address?: string;
  phone?: string;
  website?: string;
  email?: string;
  rating?: number;
  review_count?: number;
  latitude?: number;
  longitude?: number;
  link?: string;
  emails?: GoogleMapsContact[];
  phones?: GoogleMapsContact[];
  socials?: GoogleMapsSocial[];
  company?: {name?: string; country?: string; description?: string};
  domain?: {name?: string; registrar?: string; created_at?: string; expires_at?: string; country?: string};
  enrichment_status?: "completed" | "partial" | "skipped" | "failed";
  enrichment_sources?: string[];
  analysis?: {
    score?: number;
    match_reason?: string;
    sales_entry?: string;
    priority?: string;
    strategy?: string;
  };
};

export type GoogleMapsPresentation = {
  type: "google_maps_results";
  run_id?: string;
  total: number;
  center: {lat: number; lng: number};
  places: GoogleMapsPlace[];
  region?: string;
  queries?: string[];
  distance_km?: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function safeHttpURL(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length > 4096) return undefined;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? value : undefined;
  } catch {
    return undefined;
  }
}

function boundedText(value: unknown, limit: number): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, limit) : undefined;
}

function placeAddress(value: unknown): string | undefined {
  if (typeof value === "string") {
    const text = value.trim();
    if (!text.startsWith("{") && !text.startsWith("[")) return boundedText(text, 600);
    try { value = JSON.parse(text); } catch { return undefined; }
  }
  if (!isRecord(value)) return undefined;
  // 历史消息可能保存了字符串化的结构地址；空字段不显示原始 JSON。
  const parts = ["street", "borough", "city", "state", "postal_code", "country"]
    .map((key) => boundedText(value[key], 200)).filter((part): part is string => Boolean(part));
  return boundedText(parts.join(", "), 600);
}

function parseContacts(value: unknown, valueLimit: number): GoogleMapsContact[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const contacts = value.slice(0, 20).flatMap((item): GoogleMapsContact[] => {
    if (!isRecord(item)) return [];
    const contactValue = boundedText(item.value, valueLimit);
    const source = boundedText(item.source, 40);
    if (!contactValue || !source) return [];
    const confidence = finiteNumber(item.confidence);
    return [{value: contactValue, source, source_url: safeHttpURL(item.source_url), type: boundedText(item.type, 40), confidence: confidence !== undefined && confidence >= 0 && confidence <= 1 ? confidence : undefined}];
  });
  return contacts.length ? contacts : undefined;
}

function parseSocials(value: unknown): GoogleMapsSocial[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const socials = value.slice(0, 20).flatMap((item): GoogleMapsSocial[] => {
    if (!isRecord(item)) return [];
    const type = boundedText(item.type, 40);
    const url = safeHttpURL(item.url);
    return type && url ? [{type, url}] : [];
  });
  return socials.length ? socials : undefined;
}

/** 再次校验公开展示合同，第三方链接只能使用 HTTP(S)，缺失字段不生成臆测值。 */
export function parseGoogleMapsPresentation(value: unknown): GoogleMapsPresentation | null {
  if (!isRecord(value) || value.type !== "google_maps_results" || !Array.isArray(value.places)) return null;
  const center = isRecord(value.center) ? value.center : {};
  const places: GoogleMapsPlace[] = [];
  for (const raw of value.places.slice(0, 100)) {
    if (!isRecord(raw) || typeof raw.name !== "string" || !raw.name.trim()) continue;
    const latitude = finiteNumber(raw.latitude);
    const longitude = finiteNumber(raw.longitude);
    places.push({
      id: typeof raw.id === "string" ? raw.id : `${places.length}`,
      name: raw.name.slice(0, 300),
      category: boundedText(raw.category, 200),
      address: placeAddress(raw.address),
      phone: boundedText(raw.phone, 100),
      website: safeHttpURL(raw.website),
      email: boundedText(raw.email, 320),
      rating: finiteNumber(raw.rating),
      review_count: finiteNumber(raw.review_count),
      latitude: latitude !== undefined && latitude >= -90 && latitude <= 90 ? latitude : undefined,
      longitude: longitude !== undefined && longitude >= -180 && longitude <= 180 ? longitude : undefined,
      link: safeHttpURL(raw.link),
      emails: parseContacts(raw.emails, 320),
      phones: parseContacts(raw.phones, 100),
      socials: parseSocials(raw.socials),
      company: isRecord(raw.company) ? {name: boundedText(raw.company.name, 300), country: boundedText(raw.company.country, 100), description: boundedText(raw.company.description, 1000)} : undefined,
      domain: isRecord(raw.domain) ? {name: boundedText(raw.domain.name, 253), registrar: boundedText(raw.domain.registrar, 300), created_at: boundedText(raw.domain.created_at, 64), expires_at: boundedText(raw.domain.expires_at, 64), country: boundedText(raw.domain.country, 100)} : undefined,
      enrichment_status: raw.enrichment_status === "completed" || raw.enrichment_status === "partial" || raw.enrichment_status === "skipped" || raw.enrichment_status === "failed" ? raw.enrichment_status : undefined,
      enrichment_sources: Array.isArray(raw.enrichment_sources) ? raw.enrichment_sources.flatMap((item) => boundedText(item, 40) ?? []).slice(0, 10) : undefined,
      analysis: isRecord(raw.analysis) ? {
        score: finiteNumber(raw.analysis.score),
        match_reason: boundedText(raw.analysis.match_reason, 1000),
        sales_entry: boundedText(raw.analysis.sales_entry, 1000),
        priority: boundedText(raw.analysis.priority, 100),
        strategy: boundedText(raw.analysis.strategy, 1200),
      } : undefined,
    });
  }
  return {
    type: "google_maps_results",
    run_id: typeof value.run_id === "string" && /^[0-9a-f-]{36}$/i.test(value.run_id) ? value.run_id : undefined,
    total: Math.max(places.length, Math.max(0, Math.trunc(finiteNumber(value.total) ?? places.length))),
    center: {lat: finiteNumber(center.lat) ?? 0, lng: finiteNumber(center.lng) ?? 0},
    places,
    region: boundedText(value.region, 200),
    queries: Array.isArray(value.queries) ? value.queries.flatMap((item) => boundedText(item, 200) ?? []).slice(0, 20) : undefined,
    distance_km: finiteNumber(value.distance_km),
  };
}

export function googleMapsResultSummary(value: unknown, locale: string): string {
  const result = parseGoogleMapsPresentation(value);
  if (!result) return "";
  if (locale === "en") return `Found ${result.places.length} places nearby.`;
  if (locale === "zh-TW") return `已在附近找到 ${result.places.length} 個地點結果。`;
  return `已在附近找到 ${result.places.length} 个地点结果。`;
}

export function isGoogleMapsPresentation(value: unknown): boolean {
  return isRecord(value) && value.type === "google_maps_results" && Array.isArray(value.places);
}

/** 只为带明确国际区号的号码生成 WhatsApp 链接；香港本地号码依搜索地区补全。 */
function whatsappURL(phone: string, region?: string): string | undefined {
  const value = phone.trim();
  if (!/^\+?[\d\s().-]+$/.test(value)) return undefined;
  const digits = value.replace(/\D/g, "");
  const international = value.startsWith("+") ? digits : value.startsWith("00") ? digits.slice(2) : undefined;
  const number = international ?? (/(香港|Hong Kong|HK)/i.test(region ?? "") && /^[2-9]\d{7}$/.test(digits) ? `852${digits}` : undefined);
  return number && /^[1-9]\d{7,14}$/.test(number) ? `https://wa.me/${number}` : undefined;
}

function localeCopy(locale: string) {
      if (locale === "en") return {title: "Potential customers", results: "Customers", details: "View details", expand: "Open customer list", close: "Close", closePlace: "Back to list", search: "Search name, category or address", all: "All categories", contact: "Contact details", profile: "Business profile", analysis: "Analysis", score: "Match score", reason: "Why it matches", entry: "Sales entry", priority: "Priority", strategy: "Sales strategy", company: "Company", socials: "Social profiles", country: "Country", description: "Description", phone: "Phone", email: "Email", website: "Website", address: "Address", unavailable: "Not provided", whatsapp: "WhatsApp", mail: "Email", noResults: "No matching customers", reviews: "reviews", selected: "Customer details", noContact: "No contact details available", count: "results", selectAll: "Select all visible customers", select: "Select customer", addSelected: "Add to contacts", adding: "Adding...", bulkResult: (added: number, existing: number, failed: number) => `${added} added · ${existing} already saved${failed ? ` · ${failed} failed, still selected` : ""}`};
  if (locale === "zh-TW") return {title: "潛在客戶", results: "客戶列表", details: "查看詳情", expand: "展開客戶列表", close: "關閉", closePlace: "返回列表", search: "搜尋名稱、類別或地址", all: "所有類別", contact: "聯絡方式", profile: "商家資料", analysis: "分析結果", score: "匹配評分", reason: "匹配理由", entry: "銷售切入點", priority: "優先級", strategy: "合作策略", company: "公司資料", socials: "社交平台", country: "國家／地區", description: "簡介", phone: "電話", email: "電郵", website: "網站", address: "地址", unavailable: "未提供", whatsapp: "WhatsApp 聯絡", mail: "發送電郵", noResults: "沒有符合條件的客戶", reviews: "則評論", selected: "客戶詳情", noContact: "尚無聯絡方式", count: "個結果", selectAll: "選擇目前顯示的客戶", select: "選擇客戶", addSelected: "加入聯絡人", adding: "加入中…", bulkResult: (added: number, existing: number, failed: number) => `已加入 ${added} 家 · 已存在 ${existing} 家${failed ? ` · ${failed} 家失敗，已保留選取` : ""}`};
  return {title: "潜在客户", results: "客户列表", details: "查看详情", expand: "展开客户列表", close: "关闭", closePlace: "返回列表", search: "搜索名称、类别或地址", all: "所有类别", contact: "联系方式", profile: "商家资料", analysis: "分析结果", score: "匹配评分", reason: "匹配理由", entry: "销售切入点", priority: "优先级", strategy: "合作策略", company: "公司资料", socials: "社交平台", country: "国家／地区", description: "简介", phone: "电话", email: "邮箱", website: "网站", address: "地址", unavailable: "未提供", whatsapp: "WhatsApp 联系", mail: "发送邮件", noResults: "没有符合条件的客户", reviews: "条评价", selected: "客户详情", noContact: "尚无联系方式", count: "个结果", selectAll: "选择当前显示的客户", select: "选择客户", addSelected: "加入联系人", adding: "加入中…", bulkResult: (added: number, existing: number, failed: number) => `已加入 ${added} 家 · 已存在 ${existing} 家${failed ? ` · ${failed} 家失败，已保留勾选` : ""}`};
}

function contacts(place: GoogleMapsPlace, kind: "phone" | "email"): GoogleMapsContact[] {
  const values = kind === "phone" ? place.phones : place.emails;
  const fallback = kind === "phone" ? place.phone : place.email;
  return values?.length ? values : fallback ? [{value: fallback, source: "google_maps", source_url: place.link}] : [];
}

function hasContact(place: GoogleMapsPlace) {
  return Boolean(contacts(place, "phone").length || contacts(place, "email").length || place.website || place.socials?.length);
}

/** 来源只显示已返回的事实字段；客户评分与合作建议必须由后续分析节点产出。 */
function CustomerDetail({place, region, copy, locale, threadID, runID, saved = false, onSaved}: {place: GoogleMapsPlace; region?: string; copy: ReturnType<typeof localeCopy>; locale: string; threadID?: string; runID?: string; saved?: boolean; onSaved?: (id: string) => void}) {
  const [contactState, setContactState] = useState<"loading" | "ready" | "saving" | "saved" | "error" | "load_error">("loading");
  const labels = contactCopy(locale);
  useEffect(() => {
    if (!threadID) return;
    let current = true;
    listContacts().then(items => {if(current)setContactState((state) => state === "saving" || state === "saved" ? state : items.some(item=>item.place_id===place.id)?"saved":"ready");}).catch(()=>{if(current)setContactState((state) => state === "saving" || state === "saved" ? state : "load_error");});
    return ()=>{current=false;};
  }, [threadID, place.id]);
  async function saveContact() {
    if (!threadID) return;
    setContactState("saving");
    try {await addContact(threadID,place.id,runID);setContactState("saved");onSaved?.(place.id);} catch {setContactState("error");}
  }
  const phones = contacts(place, "phone");
  const emails = contacts(place, "email");
  const whatsapp = phones.map((item) => whatsappURL(item.value, region)).find(Boolean);
  const field = (label: string, content: React.ReactNode) => <div className="map-result-detail-field"><small>{label}</small>{content || <span className="is-unavailable">{copy.unavailable}</span>}</div>;
  const contactList = (items: GoogleMapsContact[], kind: "phone" | "email") => items.length ? <div className="map-result-contact-list">{items.map((item, index) => <div key={`${kind}-${item.value}-${index}`}><a href={`${kind === "phone" ? "tel" : "mailto"}:${encodeURIComponent(item.value)}`}>{item.value}</a></div>)}</div> : null;
  return <article className="map-result-workspace-detail" aria-label={copy.selected}>
    <header className="map-result-detail-heading">
      <span className="map-result-detail-monogram" aria-hidden="true">{place.name.slice(0, 2).toUpperCase()}</span>
      <div><h3>{place.name}</h3><p>{[place.category, place.address].filter(Boolean).join(" · ")}</p>
        {place.rating != null && <span className="map-result-detail-rating">★ {place.rating.toFixed(1)}{place.review_count ? ` · ${place.review_count} ${copy.reviews}` : ""}</span>}</div>
    </header>
    {threadID && <div className="map-result-detail-actions map-result-save-actions"><button className="is-primary" type="button" disabled={saved || contactState === "saving" || contactState === "saved" || contactState === "loading"} onClick={()=>void saveContact()}>{saved || contactState === "saved" ? labels.saved : contactState === "saving" ? labels.saving : labels.add}</button>{contactState === "load_error" && <span role="status">{labels.loadFailed}</span>}{contactState === "error" && <span role="alert">{labels.failed}</span>}</div>}
    <div className="map-result-detail-layout">
      {place.analysis && <section className="map-result-detail-section map-result-analysis"><h4>{copy.analysis}</h4>
        {place.analysis.score != null && <div className="map-result-analysis-score"><strong>{place.analysis.score}</strong><span>/ 100 · {copy.score}</span></div>}
        {place.analysis.match_reason && field(copy.reason, place.analysis.match_reason)}
        {place.analysis.sales_entry && field(copy.entry, place.analysis.sales_entry)}
        {place.analysis.priority && field(copy.priority, place.analysis.priority)}
        {place.analysis.strategy && field(copy.strategy, place.analysis.strategy)}
      </section>}
      <section className="map-result-detail-section"><h4>{copy.profile}</h4>
        {field(copy.address, place.address)}
        {place.company && Object.values(place.company).some(Boolean) && <>{field(copy.company, place.company.name)}{field(copy.country, place.company.country)}{field(copy.description, place.company.description)}</>}
      </section>
      <section className="map-result-detail-section"><h4>{copy.contact}</h4>
        <div className="map-result-detail-actions">{whatsapp && <a className="is-primary" href={whatsapp} target="_blank" rel="noopener noreferrer">{copy.whatsapp}</a>}{emails[0] && <a href={`mailto:${encodeURIComponent(emails[0].value)}`}>{copy.mail}</a>}</div>
        {field(copy.phone, contactList(phones, "phone"))}
        {field(copy.email, contactList(emails, "email"))}
        {field(copy.website, place.website && <a href={place.website} target="_blank" rel="noreferrer">{place.website}</a>)}
        {place.socials?.length ? <><h4>{copy.socials}</h4><div className="map-result-social-links">{place.socials.map((social, index) => <a key={`${social.type}-${index}`} href={social.url} target="_blank" rel="noreferrer">{social.type} ↗</a>)}</div></> : null}
        {!hasContact(place) && <p className="map-result-contact-empty">{copy.noContact}</p>}
      </section>
    </div>
  </article>;
}

/** 联系人工作区复用既有商家详情字段与操作，不重复维护资料布局。 */
export function ContactCustomerDetail({place,locale}:{place:GoogleMapsPlace;locale:string}) {
  return <CustomerDetail place={place} locale={locale} copy={localeCopy(locale)} saved/>;
}

/** 全屏客户工作区保留列表与详情联动，筛选后自动选择可见客户。 */
function ExpandedCustomers({result, locale, initialIndex, onClose, threadID, contactsView = false}: {result: GoogleMapsPresentation; locale: string; initialIndex: number; onClose: () => void; threadID?: string; contactsView?: boolean}) {
  const baseCopy = localeCopy(locale);
  const copy = contactsView ? {...baseCopy, title: contactCopy(locale).title, results: contactCopy(locale).title} : baseCopy;
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [mobileDetail, setMobileDetail] = useState(initialIndex >= 0);
  const categories = useMemo(() => Array.from(new Set(result.places.map((place) => place.category).filter((item): item is string => Boolean(item)))), [result.places]);
  const filtered = result.places.map((place, index) => ({place, index})).filter(({place}) => (!category || place.category === category) && `${place.name} ${place.category ?? ""} ${place.address ?? ""}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const selected = filtered.find(({index}) => index === selectedIndex) ?? filtered[0];
  const [selectedPlaces, setSelectedPlaces] = useState<Set<string>>(new Set());
  const [bulkState, setBulkState] = useState<"idle" | "saving" | "done">("idle");
  const [bulkResult, setBulkResult] = useState("");
  const [savedPlaces, setSavedPlaces] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!threadID || contactsView) return;
    let active = true;
    listContacts().then((items) => { if (active) setSavedPlaces(new Set(items.map((item) => item.place_id))); }).catch(() => {});
    return () => { active = false; };
  }, [threadID, contactsView]);
  const visibleIDs = filtered.map(({place}) => place.id).filter((id) => !savedPlaces.has(id));
  const selectedVisibleCount = visibleIDs.filter((id) => selectedPlaces.has(id)).length;
  const allVisibleSelected = visibleIDs.length > 0 && selectedVisibleCount === visibleIDs.length;
  function togglePlace(placeID: string) { if (savedPlaces.has(placeID) || bulkState === "saving") return; setSelectedPlaces((current) => { const next = new Set(current); if (next.has(placeID)) next.delete(placeID); else next.add(placeID); return next; }); }
  function toggleAllVisible() { if (bulkState === "saving") return; setSelectedPlaces((current) => { const next = new Set(current); if (allVisibleSelected) visibleIDs.forEach((id) => next.delete(id)); else visibleIDs.forEach((id) => next.add(id)); return next; }); }
  async function addSelected() {
    if (!threadID || selectedPlaces.size === 0 || bulkState === "saving") return;
    const pending = [...selectedPlaces];
    setBulkState("saving"); setBulkResult(""); let added = 0; let existing = 0; const failed = new Set<string>();
    for (const placeID of pending) {
      try {
        const saved = await addContact(threadID, placeID, result.run_id);
        if (saved.created) added++; else existing++;
        setSavedPlaces((current) => new Set(current).add(placeID));
      } catch { failed.add(placeID); }
    }
    setSelectedPlaces(failed);
    setBulkResult(copy.bulkResult(added, existing, failed.size));
    setBulkState("done");
  }

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => { document.body.style.overflow = previous; };
  }, []);

  function onKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (mobileDetail && !contactsView && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 760px)").matches) setMobileDetail(false);
      else onClose();
      return;
    }
    if (event.key !== "Tab" || !dialogRef.current) return;
    const elements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input, select, a[href]')).filter((element) => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden");
    if (!elements.length) return;
    if (event.shiftKey && document.activeElement === elements[0]) { event.preventDefault(); elements.at(-1)?.focus(); }
    if (!event.shiftKey && document.activeElement === elements.at(-1)) { event.preventDefault(); elements[0].focus(); }
  }

  return createPortal(<div className="map-result-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="map-result-workspace" role="dialog" aria-modal="true" aria-label={copy.title} ref={dialogRef} onKeyDown={onKeyDown}>
      <header className="map-result-workspace-header"><div className="map-result-workspace-heading"><h2>{copy.title}</h2><p>{[result.region, result.distance_km ? `${result.distance_km} km` : "", `${result.places.length} ${copy.count}`].filter(Boolean).join(" · ")}</p></div>
        <button className="map-result-close" ref={closeRef} type="button" title={copy.close} aria-label={copy.close} onClick={onClose}>×</button>
      </header>
      <div className="map-result-toolbar">
        <input type="search" value={search} onChange={(event) => { setSearch(event.target.value); setMobileDetail(false); }} placeholder={copy.search} aria-label={copy.search} />
        <select value={category} onChange={(event) => { setCategory(event.target.value); setMobileDetail(false); }} aria-label={copy.all}><option value="">{copy.all}</option>{categories.map((item) => <option key={item} value={item}>{item}</option>)}</select>
        <span>{filtered.length} {copy.count}</span>
      </div>
      <div className={`map-result-workspace-body ${mobileDetail ? "is-detail" : ""}`}>
        <section className="map-result-workspace-list" aria-label={copy.results}>
          <header className="map-result-workspace-list-header"><input type="checkbox" checked={allVisibleSelected} disabled={visibleIDs.length === 0 || bulkState === "saving"} onChange={toggleAllVisible} aria-label={copy.selectAll} /><strong>{copy.results} ({filtered.length})</strong>{threadID && <button className="map-result-bulk-add-inline" type="button" disabled={selectedVisibleCount === 0 || bulkState === "saving"} onClick={() => void addSelected()}>{bulkState === "saving" ? copy.adding : `${copy.addSelected} (${selectedVisibleCount})`}</button>}</header>
          {bulkResult && <p className="map-result-bulk-status" role="status">{bulkResult}</p>}
          <div className="map-result-workspace-rows">{filtered.length ? filtered.map(({place, index}) => <div className={`map-result-workspace-row-wrap ${selected?.index === index ? "is-selected" : ""}`} key={`${place.id}-${index}`}>
            <input className="map-result-row-checkbox" type="checkbox" checked={selectedPlaces.has(place.id)} disabled={bulkState === "saving" || savedPlaces.has(place.id)} onChange={() => togglePlace(place.id)} onClick={(event) => event.stopPropagation()} aria-label={`${place.name} · ${savedPlaces.has(place.id) ? contactCopy(locale).saved : copy.select}`} />
            <button className="map-result-workspace-row" type="button" onClick={() => { setSelectedIndex(index); setMobileDetail(true); }} aria-label={`${place.name} · ${copy.details}`}>
              <span className="map-result-row-index">{index + 1}</span><span className="map-result-workspace-row-copy"><strong>{place.name}</strong><small>{[place.category, place.address].filter(Boolean).join(" · ")}</small></span><span className="map-result-row-arrow" aria-hidden="true">›</span>
            </button>
          </div>) : <p className="map-result-workspace-empty">{copy.noResults}</p>}</div>
        </section>
        <div className="map-result-workspace-profile">{selected && <><button className="map-result-detail-back" type="button" onClick={() => { if (contactsView) onClose(); else setMobileDetail(false); }}>‹ {copy.closePlace}</button><CustomerDetail key={selected.place.id} place={selected.place} region={result.region} copy={copy} locale={locale} threadID={threadID} runID={result.run_id} saved={savedPlaces.has(selected.place.id)} onSaved={(id) => {setSavedPlaces((current) => new Set(current).add(id));setSelectedPlaces((current) => {const next = new Set(current);next.delete(id);return next;});}} /></>}</div>
      </div>
    </section>
  </div>, document.body);
}

/** 对话使用紧凑预览；联系人使用无框完整列表，共享详情与焦点恢复。 */
export function GoogleMapsResult({result, locale, threadID, variant = "preview", selectedIDs, onToggle, onOpenRecords,unreadCounts}: {result: GoogleMapsPresentation; locale: string; threadID?: string; variant?: "preview" | "contacts"; unreadCounts?:Map<string,number>; selectedIDs?: Set<string>; onToggle?: (id: string) => void; onOpenRecords?: (placeID:string,trigger:HTMLButtonElement)=>void}) {
  const baseCopy = localeCopy(locale);
  const copy = variant === "contacts" ? {...baseCopy, title: contactCopy(locale).title, results: contactCopy(locale).title, selectAll: locale === "en" ? "Select all visible customers" : locale === "zh-TW" ? "選擇目前顯示的客戶" : "选择当前显示的客户", select: locale === "en" ? "Select customer" : locale === "zh-TW" ? "選擇客戶" : "选择客户", addSelected: locale === "en" ? "Add selected" : locale === "zh-TW" ? "加入已選" : "加入已选", adding: locale === "en" ? "Adding..." : locale === "zh-TW" ? "加入中…" : "加入中…"} : baseCopy;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  function open(index: number, trigger: HTMLButtonElement) { triggerRef.current = trigger; setExpandedIndex(index); }
  function close() { setExpandedIndex(null); requestAnimationFrame(() => triggerRef.current?.focus()); }
  return <section className={variant === "contacts" ? "contacts-list" : "map-result"} aria-label={copy.title}>
    {variant === "preview" && <header className="map-result-preview-header"><div><strong>{copy.title}</strong><span>{result.region} {result.distance_km ? `· ${result.distance_km} km` : ""}</span></div><b>{result.places.length} {copy.count}</b></header>}
    <div className={variant === "contacts" ? "contacts-list-rows" : "map-result-list"} aria-label={copy.results}>
      {result.places.length ? result.places.map((place, index) => <div className={`${variant === "contacts" ? "contacts-list-row" : "map-result-place"}${onToggle ? " has-selection" : ""}${selectedIDs?.has(place.id) ? " is-checked" : ""}`} key={variant==="contacts"?place.id:`${place.id}-${index}`}>
        {onToggle && <input type="checkbox" aria-label={place.name} checked={selectedIDs?.has(place.id)??false} onChange={()=>onToggle(place.id)} />}
        {variant === "contacts" ? (
          // 主体与复选框切换同一选择状态，右侧按钮独立查看商家详情。
          <button className="contacts-list-main" type="button" aria-pressed={onToggle ? (selectedIDs?.has(place.id) ?? false) : undefined} aria-label={`${place.name} · ${onToggle ? (locale === "en" ? "Select contact" : locale === "zh-TW" ? "選擇聯絡人" : "选择联系人") : copy.details}`} onClick={(event) => {if(onToggle)onToggle(place.id);else open(index, event.currentTarget);}}>
            <span className="map-result-place-copy"><strong>{place.name}</strong><small>{[place.category, place.address].filter(Boolean).join(" · ")}</small></span>
            {(unreadCounts?.get(place.id)??0)>0&&<span className="mail-unread-badge" aria-label={locale==="en"?`${unreadCounts!.get(place.id)} unread messages`:locale==="zh-TW"?`${unreadCounts!.get(place.id)} 封未讀郵件`:`${unreadCounts!.get(place.id)} 封未读邮件`}>{unreadCounts!.get(place.id)!>99?"99+":unreadCounts!.get(place.id)}</span>}
          </button>
        ) : <><span className="map-result-row-index">{index + 1}</span><div className="map-result-place-copy"><strong>{place.name}</strong><small>{[place.category, place.address].filter(Boolean).join(" · ")}</small></div></>}
        <button className={variant === "contacts" ? "contacts-list-details" : undefined} type="button" onClick={(event) => {if(variant==="contacts"&&onOpenRecords)onOpenRecords(place.id,event.currentTarget);else open(index,event.currentTarget);}}>{variant==="contacts"&&onOpenRecords?(locale==="en"?"Outreach records":locale==="zh-TW"?"觸達記錄":"触达记录"):copy.details}</button>
      </div>) : <p className="map-result-workspace-empty">{copy.noResults}</p>}
    </div>
    {variant === "preview" && result.places.length > 0 && <button className="map-result-expand" type="button" onClick={(event) => open(-1, event.currentTarget)}>{copy.expand} <span aria-hidden="true">↗</span></button>}
    {expandedIndex !== null && <ExpandedCustomers result={result} locale={locale} initialIndex={expandedIndex} onClose={close} threadID={threadID} contactsView={variant === "contacts"} />}
  </section>;
}

/** 消息未改变时复用解析结果，避免聊天状态更新重建客户列表。 */
export function GoogleMapsResultPresentation({value, locale, threadID}: {value: unknown; locale: string; threadID?: string}) {
  const result = useMemo(() => parseGoogleMapsPresentation(value), [value]);
  return result ? <GoogleMapsResult result={result} locale={locale} threadID={threadID} /> : null;
}
